import React, { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { X, Loader2, Send, AlertCircle, Coins, Info, Lock } from 'lucide-react';
import type { FilterState } from '../constants';
import { createCampaign, dispatchCampaign, formatPence, estimateCampaignCostPence } from '../services/campaigns';
import { fetchSubscription, getDoorstepsLetterRatePence, getDoorstepsTier, type DoorstepsTier } from '../services/pricing';
import { useToast } from './Toast';

interface CreateCampaignModalProps {
  user: User;
  filters: FilterState;
  polygon: GeoJSON.Polygon | null;
  postcodes: string[];
  targetCount: number;
  isDemo?: boolean;
  onClose: () => void;
  onCreated: (campaignId: string) => void;
  onUpgradeRequested: () => void;
}

export default function CreateCampaignModal({
  user,
  filters,
  polygon,
  postcodes,
  targetCount,
  isDemo,
  onClose,
  onCreated,
  onUpgradeRequested,
}: CreateCampaignModalProps) {
  const [name, setName] = useState(`Campaign ${new Date().toLocaleDateString('en-GB')}`);
  const [maxTargets, setMaxTargets] = useState(Math.min(targetCount, 500));
  const [sendNow, setSendNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tier, setTier] = useState<DoorstepsTier>('free');
  const toast = useToast();

  useEffect(() => {
    fetchSubscription(user.id).then((s) => setTier(s.tier));
  }, [user.id]);

  const tierMeta = getDoorstepsTier(tier);
  const ratePence = getDoorstepsLetterRatePence(tier);
  const cost = estimateCampaignCostPence(maxTargets, tier);
  const isFree = tier === 'free';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const id = await createCampaign({
        name,
        filters,
        polygon,
        postcodes,
        maxTargets,
      });
      if (sendNow) {
        try {
          await dispatchCampaign(id);
          toast.success('Campaign queued for sending.');
        } catch (err: any) {
          toast.error(err?.message || 'Created, but sending failed. You can retry from the campaign detail page.');
        }
      } else {
        toast.success('Campaign saved as draft.');
      }
      onCreated(id);
    } catch (err: any) {
      setError(err?.message || 'Failed to create campaign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-slide-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="font-semibold text-slate-900">Create campaign</h2>
            <p className="text-xs text-slate-500">{targetCount.toLocaleString()} matching properties in your selection.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {isDemo && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <Info size={16} className="shrink-0 mt-0.5" />
              <span>
                <strong>Demo mode.</strong> The properties table is empty, so this
                will create a real campaign record with <strong>0 targets</strong>.
                Useful for previewing the QR code and landing page; you'll need to
                ingest Land Registry data before letters can be posted.
              </span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Campaign name
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Number of letters to send
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={Math.max(targetCount, 1)}
                value={maxTargets}
                onChange={(e) => setMaxTargets(Number(e.target.value))}
                className="flex-1 accent-emerald-600"
              />
              <input
                type="number"
                min={1}
                max={targetCount}
                value={maxTargets}
                onChange={(e) => setMaxTargets(Math.max(1, Math.min(targetCount, Number(e.target.value) || 1)))}
                className="w-20 px-2 py-1 border border-slate-200 rounded text-sm text-right"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              We'll pick the {maxTargets.toLocaleString()} highest-scoring properties from your selection.
            </p>
          </div>

          {isFree ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <Lock size={20} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900 flex-1">
                <div className="font-semibold mb-0.5">Sending is locked on the Free tier</div>
                <div className="text-xs text-amber-800">
                  You can save this as a draft and design the letter, but you'll need a paid plan to actually post it.
                  Starter is £49/mo + £1.25/letter.
                </div>
                <button
                  type="button"
                  onClick={onUpgradeRequested}
                  className="mt-2 text-xs font-semibold text-amber-900 hover:text-amber-700 underline"
                >
                  See pricing →
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
              <Coins size={20} className="text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-900">
                <div className="font-semibold">Estimated cost: {formatPence(cost)}</div>
                <div className="text-xs text-emerald-700">
                  {maxTargets} letters × {formatPence(ratePence)} on your <strong>{tierMeta.name}</strong> plan (printed, enveloped, posted second-class)
                </div>
              </div>
            </div>
          )}

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={sendNow}
              disabled={isFree}
              onChange={(e) => setSendNow(e.target.checked)}
              className="mt-0.5 accent-emerald-600 disabled:opacity-40"
            />
            <span className={`text-sm ${isFree ? 'text-slate-400' : 'text-slate-700'}`}>
              Send immediately. (Otherwise saved as draft — you can review and send from the campaign detail page.)
            </span>
          </label>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || maxTargets < 1}
              className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sendNow ? 'Create & send' : 'Save as draft'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
